import { useCallback, useMemo } from "react";

import type {
  PersonNameChildAttribute,
  PersonNameFieldKey,
  PersonNameValue,
} from "./types";

/**
 * 氏名フィールドの表示順序
 */
const FIELD_ORDER: PersonNameFieldKey[] = [
  "familyName",
  "givenName",
  "familyNameKana",
  "givenNameKana",
];

/**
 * childAttributesからフィールドを取得するヘルパー
 */
function getChildByFieldKey(
  children: PersonNameChildAttribute[],
  fieldKey: PersonNameFieldKey,
): PersonNameChildAttribute | undefined {
  return children.find((c) => c.fieldKey === fieldKey);
}

/**
 * 氏名フィールドの設定を表す型
 */
export type PersonNameFieldSetting = {
  fieldKey: PersonNameFieldKey;
  attributeKey: string;
  label: string;
  required: boolean;
  readonly: boolean;
};

type UsePersonNameOptions = {
  /** 氏名の値 */
  value: PersonNameValue;
  /** 値変更時のコールバック */
  onChange: (value: PersonNameValue) => void;
  /** 子属性（各フィールドのname/required/readonlyを個別に指定） */
  childAttributes?: PersonNameChildAttribute[];
  /** コンポーネント全体のreadonlyフラグ */
  readonly?: boolean;
};

/**
 * 氏名入力のロジックを管理するフック
 *
 * 主な責務:
 * - フィールド設定の解決（childAttributesとデフォルト値のマージ）
 * - 値の更新ハンドラ
 * - 表示用のフォーマット済み値
 */
export const usePersonName = ({
  value,
  onChange,
  childAttributes = [],
  readonly = false,
}: UsePersonNameOptions) => {
  // 名前フィールド（姓・名）の設定を取得
  const nameFields = useMemo(() => {
    const familyNameChild = getChildByFieldKey(childAttributes, "familyName");
    const givenNameChild = getChildByFieldKey(childAttributes, "givenName");

    return [familyNameChild, givenNameChild].filter(
      (c): c is PersonNameChildAttribute => c !== undefined,
    );
  }, [childAttributes]);

  // カナフィールド（姓カナ・名カナ）の設定を取得
  const kanaFields = useMemo(() => {
    const familyNameKanaChild = getChildByFieldKey(
      childAttributes,
      "familyNameKana",
    );
    const givenNameKanaChild = getChildByFieldKey(
      childAttributes,
      "givenNameKana",
    );

    return [familyNameKanaChild, givenNameKanaChild].filter(
      (c): c is PersonNameChildAttribute => c !== undefined,
    );
  }, [childAttributes]);

  // 全フィールドの設定（順序保証）
  const allFields = useMemo(() => {
    return FIELD_ORDER.map((fieldKey) =>
      getChildByFieldKey(childAttributes, fieldKey),
    ).filter((c): c is PersonNameChildAttribute => c !== undefined);
  }, [childAttributes]);

  // フィールドがreadonlyかどうかを判定
  const isFieldReadonly = useCallback(
    (child: PersonNameChildAttribute) => {
      return child.readonly || readonly;
    },
    [readonly],
  );

  // 単一フィールドの値を更新
  const handleFieldChange = useCallback(
    (fieldKey: PersonNameFieldKey, newValue: string) => {
      onChange({
        ...value,
        [fieldKey]: newValue,
      });
    },
    [value, onChange],
  );

  // 表示用のフォーマット済み氏名
  const displayName = useMemo(() => {
    return [value.familyName, value.givenName].filter(Boolean).join(" ");
  }, [value.familyName, value.givenName]);

  // 表示用のフォーマット済みカナ
  const displayNameKana = useMemo(() => {
    return [value.familyNameKana, value.givenNameKana]
      .filter(Boolean)
      .join(" ");
  }, [value.familyNameKana, value.givenNameKana]);

  return {
    /** 名前フィールド（姓・名）の設定 */
    nameFields,
    /** カナフィールド（姓カナ・名カナ）の設定 */
    kanaFields,
    /** 全フィールドの設定（順序保証） */
    allFields,
    /** フィールドがreadonlyかどうかを判定 */
    isFieldReadonly,
    /** 単一フィールドの値を更新 */
    handleFieldChange,
    /** 表示用のフォーマット済み氏名 */
    displayName,
    /** 表示用のフォーマット済みカナ */
    displayNameKana,
  };
};
